import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styles from './FJSlider.module.less';
import classNames from 'classnames';
import FJUtil from '../../../util/FJUtil';

const FJSlider = ({
    min = 0,
    max = 100,
    value = 50,
    onChange,
    className,
    thumbSize = 16,
    disabled = false,
    callback,
}) => {
    const [sliderValue, setSliderValue] = useState(value);
    const sliderRef = useRef(null);
    const thumbRef = useRef(null);

    useEffect(() => {
        setSliderValue(value);
    }, [value]);

    const setValueInRange = useCallback(
        (e, type = 'mousedown') => {
            const slider = sliderRef.current;
            const rect = slider.getBoundingClientRect();

            const newValue = Math.min(Math.max(((e.clientX - rect.left) / rect.width) * (max - min) + min, min), max);
            setSliderValue(newValue);
            if (onChange) {
                onChange(newValue, type);
            }
            if (callback) {
                switch (type) {
                    case 'mousedown':
                        callback({
                            type: 'slideStart',
                            value: newValue,
                        });
                        break;
                    case 'mousemove':
                        callback({
                            type: 'changed',
                            value: newValue,
                        });
                        break;
                    case 'mouseup':
                        callback({
                            type: 'slideStop',
                            value: newValue,
                        });
                        break;
                }
            }
        },
        [onChange, sliderRef.current, max, min],
    );
    const handleMouseMove = useCallback(
        e => {
            setValueInRange(e, 'mousemove');
        },
        [setValueInRange],
    );

    const _moveThrottle = useCallback(FJUtil.throttle(handleMouseMove, 10), [handleMouseMove]);

    const handleMouseUp = useCallback(
        e => {
            setValueInRange(e, 'mouseup');

            document.removeEventListener('mousemove', _moveThrottle);
            document.removeEventListener('mouseup', handleMouseUp);
        },
        [_moveThrottle],
    );

    const handleMouseDown = useCallback(
        e => {
            if (disabled) return;
            setValueInRange(e, 'mousedown');

            document.addEventListener('mousemove', _moveThrottle);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [handleMouseUp, _moveThrottle, disabled],
    );

    const thumbLeft = useMemo(() => {
        return ((sliderValue - min) / (max - min)) * 100;
    }, [sliderValue, min, max]);

    return (
        <div className={classNames(styles.slider_box, className)} onMouseDown={handleMouseDown}>
            <div className={styles.slider} ref={sliderRef}>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${((sliderValue - min) / (max - min)) * 100}%`,
                        height: '100%',
                        backgroundColor: 'black',
                        borderRadius: '100px',
                    }}
                />
                <div className={styles.thumb_box} style={{ width: `calc(100% - ${thumbSize - 1}px)` }}>
                    <div
                        className={styles.thumb}
                        ref={thumbRef}
                        style={{ left: `${thumbLeft}%`, width: `${thumbSize}px`, height: `${thumbSize}px` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default FJSlider;
